import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';

import { jwtDecode } from 'jwt-decode';

import { MenuItem } from 'primeng/api';

import { lastValueFrom, Subscription } from 'rxjs';

import { CognitoSelector } from '../../state/selectors/cognito-selector';
import { AuthProfileService } from '../../services/auth-profile.service';
import { environment } from '../../../../../environments/environment';

/**
 * `menu.json` entry shape: a plain `MenuItem` (what `p-menubar` actually renders) plus the two
 * fields this component reads before handing the array to PrimeNG - `labelKey` (i18n key,
 * resolved into `label`) and `multimediaOnly` (role gate, see `filterMultimediaOnly`). Not the
 * `Menu` class from `emed-ui-shared` - that type carries required getters (`children`,
 * `isLeaf`, `route`) meant for its own NGXS-backed menu components, which plain JSON fetched via
 * `HttpClient` never has.
 */
interface MenuEntry extends MenuItem {
  labelKey?: string;
  multimediaOnly?: boolean;
  items?: MenuEntry[];
}

/** `menu.json` entry key used to gate the whole "Configurazione" top-level menu by the Cognito `Configurazione` group. */
const CONFIGURAZIONE_LABEL_KEY = 'menu.CONFIGURAZIONE';

@Component({
  selector: 'emed-main-dispatcher',
  templateUrl: './emed-main-dispatcher.component.html',
  styleUrl: './emed-main-dispatcher.component.scss',
  standalone: false
})
export class EmedMainDispatcherComponent implements OnInit, OnDestroy {
  protected isAuthenticated = false;
  protected accessToken: any;
  protected isConfigurazione: boolean;
  private subArray: Subscription[] = [];
  public menuItems: MenuEntry[] = [];

  /** Menu structure filtered by role but with `labelKey`s not yet resolved - kept so `onLangChange` can re-resolve labels without re-fetching/re-filtering. */
  private filteredMenu: MenuEntry[] = [];

  constructor(
    protected cognitoSelector: CognitoSelector,
    private http: HttpClient,
    private translate: TranslateService,
    private authProfileService: AuthProfileService
  ) {}

  ngOnDestroy(): void {
    this.subArray.forEach((x) => x.unsubscribe());
  }

  ngOnInit(): void {
    this.subArray.push(
      this.cognitoSelector.isAuthenticated$.subscribe((data) => {
        this.isAuthenticated = data;
      })
    );

    this.subArray.push(
      this.cognitoSelector.accessToken$.subscribe(async (data) => {
        // ponytail: decode failure (e.g. noAuth dev mode has no real token) must not block
        // the menu.json fetch below - they used to share one try/catch, so a bad token
        // silently hid the whole menu.
        try {
          this.accessToken = jwtDecode(data);
          console.log('accessToken: ' + JSON.stringify(this.accessToken));

          if (this.accessToken && this.accessToken['cognito:groups']) {
            const index = this.accessToken['cognito:groups'].findIndex((data: string) => data == 'Configurazione');
            this.isConfigurazione = index >= 0 ? true : false;
          }
        } catch (Error) {
          // no valid token to decode - isConfigurazione stays false, menu below still loads
        }

        // ponytail: noAuth has no real cognito:groups to check, so the group-derived flag
        // above never becomes true - without this, the "Configurazione" menu section (and
        // everything under it) is permanently unreachable in local dev, same failure as the
        // isAuthenticated one AppComponent.noAuth branch fixes.
        if (environment.noAuth) {
          this.isConfigurazione = true;
        }

        // Piano Fase 6: il menu va caricato per ogni utente autenticato, non solo per il
        // gruppo Cognito 'Configurazione' - "Contatti" self-service (Gruppo/Azienda/Sede/
        // SWHouse) deve restare raggiungibile anche per chi non appartiene a quel gruppo.
        // La voce "Configurazione" (CRUD amministrativi) resta filtrata via `isConfigurazione`.
        //
        // Fase 1 (gap I): le singole voci sotto "Configurazione" che puntano a rotte protette
        // da `ConfigurazioneGuard` con `data: { multimediaOnly: true }` (tipo-documento,
        // tipo-transazione, listino, service-scope) devono restare nascoste a chi è nel gruppo
        // Cognito 'Configurazione' ma non in 'Multimedia' - altrimenti l'utente vede la voce e
        // finisce su /unauthorized al click. Filtrate qui via lo stesso predicato usato dalla
        // guard, `AuthProfileService.isMultimediaSnapshot()`, tramite il flag `multimediaOnly`
        // sulle singole entry di menu.json.
        const menu = await lastValueFrom(this.http.get<MenuEntry[]>('./assets/configuration/menu.json'));
        // noAuth has no real token for isMultimediaSnapshot() to read (same reason
        // isConfigurazione is forced above) - ConfigurazioneGuard already short-circuits to
        // "allowed" for noAuth, so the menu must show the same entries it would let you open.
        const isMultimedia = environment.noAuth ? true : this.authProfileService.isMultimediaSnapshot();

        this.filteredMenu = menu
          .filter((item) => item.labelKey !== CONFIGURAZIONE_LABEL_KEY || this.isConfigurazione)
          .map((item) => this.filterMultimediaOnly(item, isMultimedia))
          .filter((item): item is MenuEntry => item !== null);

        this.menuItems = await this.resolveMenuLabels(this.filteredMenu);
      })
    );

    // Fase 1 (i18n): menu.json ships `labelKey`s, not literal text - re-resolve them whenever
    // the active language changes so the menu doesn't stay stuck in whatever language was
    // active when it was first fetched.
    this.subArray.push(
      this.translate.onLangChange.subscribe(async () => {
        if (this.filteredMenu.length > 0) {
          this.menuItems = await this.resolveMenuLabels(this.filteredMenu);
        }
      })
    );
  }

  /**
   * Drops `item` (and, recursively, its children) when it is flagged `multimediaOnly` and the
   * current user isn't in the Multimedia group - the same predicate `ConfigurazioneGuard` uses,
   * so the menu can never show an entry the guard would then reject.
   */
  private filterMultimediaOnly(item: MenuEntry, isMultimedia: boolean): MenuEntry | null {
    if (item.multimediaOnly && !isMultimedia) {
      return null;
    }

    if (item.items?.length) {
      return {
        ...item,
        items: item.items.map((child) => this.filterMultimediaOnly(child, isMultimedia)).filter((child): child is MenuEntry => child !== null)
      };
    }

    return item;
  }

  /** Resolves each entry's `labelKey` (via the i18n loader) into the `label` PrimeNG's `p-menubar` renders. */
  private async resolveMenuLabels(items: MenuEntry[]): Promise<MenuEntry[]> {
    const resolved: MenuEntry[] = [];

    for (const item of items) {
      const clone: MenuEntry = { ...item };

      if (item.labelKey) {
        clone.label = await lastValueFrom(this.translate.get(item.labelKey));
      }

      if (item.items?.length) {
        clone.items = await this.resolveMenuLabels(item.items);
      }

      resolved.push(clone);
    }

    return resolved;
  }
}
